import {useCallback, useEffect, useRef, useState} from "react";
import { Environment } from "../../../enums/Environment";
import { kFakeTerms } from "../../../services/Faker";
import { isArrayNullUndefinedOrEmpty, isNullOrUndefined } from "../../../services/Validator";
import { kApiUnknownError, kEmptyResponseError } from "../../../constants/errors";
import { sendErrorToSentry } from "../../../services/Sentry";
import { get_terms } from "../../../api/term";
import SystemScreenHeader from "../../headers/SystemScreenHeader";
import GlossaryCoreContent from "./GlossaryCoreContent";

export default function GlossaryContent() {
    // Used in GlossaryScreen to display core content made of
    // research bar, letters list and terms list

    // PROPERTIES
    // ---- Meta
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // ---- functional
    const [terms, setTerms] = useState(null);
    const [filteredTerms, setFilteredTerms] = useState(null);
    // ---- search
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [isStrictMode, setIsStrictMode] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const refs = useRef({});
    const searchTimeout = useRef(null);
    const getTerms = useCallback(() => {
        // <<<< ---- FAKER ---- -----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            setTerms(kFakeTerms)
            setIsLoading(false)
            setError(null)
            return
        }
        // ---- ---- FAKER ---- >>>>

        // Set loader on and clear potential error
        setIsLoading(true)
        setError(null)
        // Perform
        get_terms()
            .then((res) => {
                if (isNullOrUndefined(res) || isArrayNullUndefinedOrEmpty(res)) {
                    setError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, 'fetching Terms in GlossaryContent')
                } else {
                    setTerms(res)
                }
            })
            .catch((err) => {
                console.error(err)
                setError(kApiUnknownError)
                sendErrorToSentry(kApiUnknownError.sentry, 'fetching Terms in GlossaryContent')
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    // HOOKS
    // ---- Triggered at rendering : set Terms
    useEffect(() => {
        getTerms()
    }, [getTerms])

    // ---- Triggered at rendering : scroll button
    useEffect(() => {
        const container = document.getElementById("glossary-layout");
        if (!container) return;

        const handleScroll = () => setShowScrollTop(container.scrollTop > 200);
        container.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => container.removeEventListener("scroll", handleScroll);
    }, []);

    // ---- Triggered at rendering : set filtered terms
    useEffect(() => {
        if (!terms) return;

        const query = debouncedSearch.trim().toLowerCase();
        if (query === "") {
            setFilteredTerms(null);
            return;
        }

        const newFiltered = {};
        Object.keys(terms).forEach((letter) => {
            const filteredEntries = terms[letter].filter((entry) => {
                const word = entry.word.toLowerCase();
                const definition = entry.definition?.toLowerCase() ?? "";
                return isStrictMode
                    ? word.startsWith(query)
                    : word.includes(query) || definition.includes(query);
            });
            if (filteredEntries.length > 0) {
                newFiltered[letter] = filteredEntries;
            }
        });

        setFilteredTerms(newFiltered);
    }, [debouncedSearch, isStrictMode, terms]);

    // ---- Triggered at rendering : searchTimeout
    useEffect(() => {
        return () => clearTimeout(searchTimeout.current);
    }, []);

    // BUILD
    return (
        <div className="animate-appearSlow w-full flex flex-col justify-start items-center">
            <SystemScreenHeader title={"Glossaire mathématique"}
                                subtitle={"Retrouve rapidement la définition de tous les termes mathématiques dont tu as besoin."} />
            <GlossaryCoreContent error={error}
                                 getTerms={getTerms}
                                 isLoading={isLoading}
                                 filteredTerms={filteredTerms}
                                 terms={terms}
                                 search={search}
                                 handleSearchChange={handleSearchChange}
                                 isSearching={isSearching}
                                 isStrictMode={isStrictMode}
                                 setIsStrictMode={setIsStrictMode}
                                 scrollToLetter={scrollToLetter}
                                 debouncedSearch={debouncedSearch}
                                 refs={refs}
                                 highlight={highlight}
                                 showScrollTop={showScrollTop}
                                 scrollToTop={scrollToTop} />
        </div>
    );

    // METHODS
    // ---- UX : Highlight searched text
    function highlight(text, search) {
        const cleanSearch = search.trim();
        if (!cleanSearch) return text;
        const regex = new RegExp(`(${cleanSearch})`, "gi");
        return text.replace(regex, "<mark>$1</mark>");
    }

    // ---- UX : Scroll to letter
    function scrollToLetter(letter) {
        if (refs.current[letter]) {
            refs.current[letter].scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    // ---- UX : Scroll to top
    function scrollToTop() {
        const container = document.getElementById("glossary-layout");
        if (container) container.scrollTo({ top: 0, behavior: "smooth" });
    }

    // ---- LOGIC : Handle search change
    function handleSearchChange(value) {
        setSearch(value);
        setIsSearching(true); // active le petit loader

        if (searchTimeout.current) clearTimeout(searchTimeout.current);

        searchTimeout.current = setTimeout(() => {
            setDebouncedSearch(value);
            setIsSearching(false); // désactive après le debounce
        }, 400);
    }

}
